import type { Config } from "wagmi"
import { reaction } from "mobx"

import type { EmitEvent } from "@/domains/core"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { IChunkedRpcStrategy } from "@/domains/library/rpc-strategy"
import type { IApprovalModeProvider } from "@/domains/settings/interfaces"
import type { TokenRepository } from "@/domains/tokens"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import { IEventBus } from "@/domains/events"
import { EventEmitter } from "@/domains/library"

import {
  ManualClPositionGraphQlTransport,
  ManualClPositionOnChainTransport,
  ManualClPositionOrchestrator,
  ManualClPositionRepository,
} from "@/domains/thick-v1/positions"
import { BurnPositionStore } from "@/domains/thick-v1/stores/burn-position-store"
import { CollectFeesStore } from "@/domains/thick-v1/stores/collect-fees-store"
import { CreatePositionStore } from "@/domains/thick-v1/stores/create-position-store"
import { DecreasePositionStore } from "@/domains/thick-v1/stores/decrease-position-store"
import { IncreasePositionStore } from "@/domains/thick-v1/stores/increase-position-store"
import { IndexerStatusStore } from "@/domains/thick-v1/stores/indexer-status-store"
import { PoolActivityStore } from "@/domains/thick-v1/stores/pool-activity-store"
import { PoolOhlcStore } from "@/domains/thick-v1/stores/pool-ohlc-store"
import { PositionHistoryStore } from "@/domains/thick-v1/stores/position-history-store"
import {
  ThickV1TickDistributionGraphQlTransport,
  ThickV1TickDistributionOrchestrator,
  ThickV1TickDistributionStore,
} from "@/domains/thick-v1/tick-distribution"

import type { ManualClGraphQlService } from "./manual-cl-graphql-service"
import { ManualClGraphQlTransport } from "./manual-cl-graphql-transport"
import { ManualClOrchestratorFactory } from "./manual-cl-orchestrator-factory"
import { ManualClPoolRepository } from "./manual-cl-repository"
import { CreatePoolStore } from "./stores/create-pool-store"

/**
 * Subscriber called on every route change with the current `:id` URL segment
 * (undefined when the user leaves the manage sub-tree). Manage-position stores
 * register themselves here so that one `useParams()` in
 * `ManagePositionPageComposite` fans out to every store without each
 * sub-composite taking its own router dependency.
 */
export type ActiveManagePositionTokenIdSubscriber = (tokenIdParam: string | undefined) => void

export type ManualClV1Namespace = {
  Repository: ManualClPoolRepository
  Orchestrator: ReturnType<typeof ManualClOrchestratorFactory.create>
  CreatePool: CreatePoolStore
  CreatePosition: CreatePositionStore
  /**
   * Shared tick-distribution store for the liquidity-profile range brush.
   *
   * A MobX reaction keeps its `activePool` aligned with whatever pool the
   * create-position (and later, manage-position) flow has loaded, so the
   * composite stays a pure observer with no imperative wiring at the page
   * level. Final ownership moves under `scaleApp.ThickV1` in t32; exposing
   * it here unblocks the create-position range brush today.
   */
  TickDistribution: ThickV1TickDistributionStore
  /**
   * Observable repository of the connected wallet's Manual CL position NFTs.
   * Orchestrator below is responsible for hydrating it from GraphQL and
   * refreshing on-chain stats on a cadence; UI observes `activePositions` +
   * `isHydrated` directly. Final ownership moves under `scaleApp.ThickV1`
   * in t32; exposing it here unblocks the my-positions table today.
   */
  Positions: ManualClPositionRepository
  /**
   * Position orchestrator reference, primarily kept for tests and for the
   * emitter namespace to force-refresh after interactions. UI should prefer
   * reading through the repository.
   */
  PositionsOrchestrator: ManualClPositionOrchestrator
  /**
   * Shared UI-toast emitter. Interactions triggered outside of the manage
   * stores (e.g. the my-positions "Claim" button) post progress/success/
   * error events through this emitter so the global `emitter` bridge still
   * sees them.
   */
  Emitter: EventEmitter<EmitEvent>
  /**
   * Manage-position stores. The four stores share a common base that owns
   * `activeTokenId`, `slippage`, and `deadline`, so the settings popover
   * wires once and every tab sees the new values. Each is a pure observer
   * target — no hooks at the page level.
   *
   * Exposed here (rather than under `scaleApp.ThickV1`) because `t32` has
   * not yet relocated the namespace; `t31`'s manage pages need them today.
   */
  Increase: IncreasePositionStore
  Decrease: DecreasePositionStore
  Collect: CollectFeesStore
  Burn: BurnPositionStore
  /**
   * Per-pool activity feed — Swap / Mint / Burn / Collect. The store is
   * shared across routes (`setActivePool` invalidates the in-flight fetch
   * on navigation) so the detail page doesn't pay a fresh-instance cost
   * every visit.
   */
  PoolActivity: PoolActivityStore
  /**
   * Per-pool OHLC + volume/fees. Loaded on demand; `setWindow` swaps the
   * lookback between 24h / 7d / 30d for the chart.
   */
  PoolOhlc: PoolOhlcStore
  /**
   * Indexer freshness telemetry. Polls Envio's built-in `chain_metadata`
   * table every 10s and compares `block_height` (head) against
   * `latest_processed_block` (progressBlock — committed-to-DB). The
   * banner stays hidden while we're within the fresh window, when the
   * endpoint is unreachable, or when the table is missing.
   */
  IndexerStatus: IndexerStatusStore
  /**
   * Per-position merged history feed (Mint / Burn / Collect) rendered on
   * the manage-position detail page. The composite drives `setActivePositionId`
   * on mount / unmount so stale fetches can't leak into unmounted views.
   */
  PositionHistory: PositionHistoryStore
  /**
   * Push the current `:id` route segment into every registered manage store
   * base. Called by `ManagePositionPageComposite`'s mount/unmount effect on
   * each route change. Safe to call with `undefined` — stores treat that as
   * "no active position".
   */
  setActiveManagePositionTokenId: (tokenIdParam: string | undefined) => void
  /**
   * Registration hook used by `ManagePositionStoreBase` subclasses to hear
   * about route changes without taking a router dependency themselves.
   * Returns a disposer in case a store ever needs to unsubscribe.
   */
  subscribeToActiveManagePositionTokenId: (subscriber: ActiveManagePositionTokenIdSubscriber) => () => void
}

export type ManualClV1NamespaceFactoryParams = {
  graphQlService: ManualClGraphQlService
  tokenRepository: TokenRepository
  orchestratedQueryStore: OrchestratedQueryStore
  eventBus: IEventBus
  chainId: number
  config: Config
  wallet: IWalletConnectionStore
  approvalModeProvider: IApprovalModeProvider
  chunkedRpcStrategy: IChunkedRpcStrategy
}

export const ManualClV1NamespaceFactory = {
  create(params: ManualClV1NamespaceFactoryParams): ManualClV1Namespace {
    const {
      graphQlService,
      tokenRepository,
      orchestratedQueryStore,
      eventBus,
      chainId,
      config,
      wallet,
      approvalModeProvider,
      chunkedRpcStrategy,
    } = params

    // Create transport
    const graphQlTransport = new ManualClGraphQlTransport(graphQlService)

    // Create repository
    const repository = new ManualClPoolRepository()

    // Create orchestrator
    const orchestrator = ManualClOrchestratorFactory.create(
      graphQlTransport,
      repository,
      tokenRepository,
      orchestratedQueryStore,
      eventBus,
    )

    // Create EventEmitter for UI notifications (singleton pattern)
    // This is separate from EventBus which handles domain events
    const emitter = new EventEmitter<EmitEvent>()

    // Create create pool store
    const createPoolStore = new CreatePoolStore(
      orchestrator,
      repository,
      tokenRepository,
      chainId,
      config,
      eventBus,
      emitter,
    )

    // Tick distribution: shared observable store + dedicated orchestrator.
    //
    // A single reaction mirrors the create-position flow's `pool` + `tickSpacing`
    // into `setActivePool`; the orchestrator's own reaction on `composeKey`
    // then drives GraphQL fetches. Intentionally no `useEffect` anywhere — the
    // reactive layer is entirely MobX, which is what the create-position
    // range-brush composite observes.
    //
    // Constructed before `CreatePositionStore` so the latter can take a
    // direct reference for its range-aware `estimatedApr` computed — the
    // orchestrator below still closes over the create-position store via
    // a thunk, preserving the previous wiring order for fetches.
    const tickDistributionStore = new ThickV1TickDistributionStore()
    const tickDistributionTransport = new ThickV1TickDistributionGraphQlTransport(graphQlService)

    // Create create position store
    const createPositionStore = new CreatePositionStore(
      orchestrator,
      repository,
      chainId,
      config,
      eventBus,
      emitter,
      wallet,
      approvalModeProvider,
      tickDistributionStore,
    )

    const tickDistributionOrchestrator = new ThickV1TickDistributionOrchestrator(
      tickDistributionStore,
      tickDistributionTransport,
      eventBus,
      orchestratedQueryStore,
      () => createPositionStore.currentTick,
      chainId,
    )
    tickDistributionOrchestrator.initialize()

    // Positions subdomain: owns the connected wallet's position NFT set.
    //
    // The orchestrator hydrates `ManualClPositionRepository` from GraphQL on
    // wallet-connect / feature-flag-enabled and keeps on-chain stats fresh via
    // a chunked RPC transport. UI observes the repository directly — no
    // React-Query, no hooks, no useEffect. Final ownership moves under
    // `scaleApp.ThickV1` in t32; exposing it here unblocks the my-positions
    // table today.
    const positionRepository = new ManualClPositionRepository()
    const positionGraphQlTransport = new ManualClPositionGraphQlTransport(graphQlService)
    const positionOnChainTransport = new ManualClPositionOnChainTransport(config, chunkedRpcStrategy)
    const positionOrchestrator = new ManualClPositionOrchestrator(
      positionRepository,
      repository,
      positionGraphQlTransport,
      positionOnChainTransport,
      wallet,
      eventBus,
      orchestratedQueryStore,
      chainId,
      tickDistributionStore,
    )
    positionOrchestrator.initialize()

    // Manage-position stores. Each subscribes to the active tokenId via
    // `subscribeToActiveManagePositionTokenId` below so that a single
    // `useParams()` effect in `ManagePositionPageComposite` drives all four.
    const increasePositionStore = new IncreasePositionStore(
      positionRepository,
      chainId,
      config,
      eventBus,
      emitter,
      wallet,
      approvalModeProvider,
    )
    const decreasePositionStore = new DecreasePositionStore(
      positionRepository,
      chainId,
      config,
      eventBus,
      emitter,
      wallet,
    )
    const collectFeesStore = new CollectFeesStore(
      positionRepository,
      chainId,
      config,
      eventBus,
      emitter,
      wallet,
    )
    const burnPositionStore = new BurnPositionStore(
      positionRepository,
      chainId,
      config,
      eventBus,
      emitter,
      wallet,
    )

    // Pool-scoped on-demand stores (detail page). Not started at construction
    // — the composite that owns the route calls `setActivePool()` when the
    // page mounts and again with `undefined` when it unmounts, so the stores
    // only fetch while the detail surface is actually on screen.
    const poolActivityStore = new PoolActivityStore(graphQlTransport)
    const poolOhlcStore = new PoolOhlcStore(graphQlTransport)

    // Indexer freshness. Start polling immediately: the banner is globally
    // useful (staleness affects every surface) and costs one light query
    // every 10s. `isHidden` stays true pre-Proposal 6 so there is no UI
    // impact until the entity lands.
    const indexerStatusStore = new IndexerStatusStore(graphQlTransport, chainId)
    indexerStatusStore.start()

    // Per-position history feed. Fetches on demand via the position-domain
    // GraphQL transport, so the manage-position history tab owns its data
    // lifecycle without taking a direct dependency on the service layer.
    const positionHistoryStore = new PositionHistoryStore(positionGraphQlTransport)

    reaction(
      () => {
        const pool = createPositionStore.pool
        const tickSpacing = createPositionStore.tickSpacing
        if (!pool || !tickSpacing) return undefined
        return { address: pool.address.raw as string, tickSpacing }
      },
      (next) => {
        if (!next) {
          tickDistributionStore.setActivePool(undefined, tickDistributionStore.tickSpacing)
          return
        }
        tickDistributionStore.setActivePool(next.address, next.tickSpacing)
      },
      { fireImmediately: true },
    )

    // Active manage-position tokenId fan-out. Kept as a plain Set of
    // subscribers rather than a MobX observable so that
    // `ManagePositionPageComposite`'s single `useEffect` is the only place
    // route state enters MobX land — subscribers are manage stores that
    // translate the tokenId into their own observable setter.
    const subscribers = new Set<ActiveManagePositionTokenIdSubscriber>()
    let lastTokenIdParam: string | undefined = undefined

    const setActiveManagePositionTokenId = (tokenIdParam: string | undefined): void => {
      if (tokenIdParam === lastTokenIdParam) return
      lastTokenIdParam = tokenIdParam
      for (const subscriber of subscribers) {
        subscriber(tokenIdParam)
      }
    }

    const subscribeToActiveManagePositionTokenId = (
      subscriber: ActiveManagePositionTokenIdSubscriber,
    ): (() => void) => {
      subscribers.add(subscriber)
      // Replay the last value so late-registering stores get current state
      // without needing their own useEffect or initial-mount wiring.
      subscriber(lastTokenIdParam)
      return () => {
        subscribers.delete(subscriber)
      }
    }

    // Fan the active tokenId out to every manage store base. Each base
    // exposes `setActiveTokenId(id)` which is idempotent on repeated calls
    // with the same id, so replay on late-registration is safe.
    subscribeToActiveManagePositionTokenId((tokenIdParam) => {
      increasePositionStore.setActiveTokenId(tokenIdParam)
      decreasePositionStore.setActiveTokenId(tokenIdParam)
      collectFeesStore.setActiveTokenId(tokenIdParam)
      burnPositionStore.setActiveTokenId(tokenIdParam)
    })

    return {
      Repository: repository,
      Orchestrator: orchestrator,
      CreatePool: createPoolStore,
      CreatePosition: createPositionStore,
      TickDistribution: tickDistributionStore,
      Positions: positionRepository,
      PositionsOrchestrator: positionOrchestrator,
      Emitter: emitter,
      Increase: increasePositionStore,
      Decrease: decreasePositionStore,
      Collect: collectFeesStore,
      Burn: burnPositionStore,
      PoolActivity: poolActivityStore,
      PoolOhlc: poolOhlcStore,
      IndexerStatus: indexerStatusStore,
      PositionHistory: positionHistoryStore,
      setActiveManagePositionTokenId,
      subscribeToActiveManagePositionTokenId,
    }
  },
}
